import type { ServiceCategoryRow } from '@/api/booking/getServiceCategoriesQuery';
import type { Service } from '@/types/booking';
import { getL1CategoriesWithSubcategories } from '@/utils/booking/getL1CategoriesWithSubcategories';
import { hasBarberAssignment } from '@/utils/booking/hasBarberAssignment';
import { isAddonService } from '@/utils/isAddonService';

/** Most standalone services a shop can have and still get the flat list. */
export const FLAT_LIST_MAX_SERVICES = 10;

export type ServiceListMode = 'flat' | 'categories' | 'plain';

interface GetServiceListModeParams {
  /** With uncategorized services already assigned to `Other`. */
  services: Service[];
  rows: ServiceCategoryRow[];
  /** True for "any professional", as in the L1 category list. */
  requireBarberAssignment: boolean;
}

/**
 * Add-ons are not counted, so the mode does not depend on the cart. With
 * `requireBarberAssignment`, services no professional offers are not counted
 * either, as the any-professional list never shows them. `plain` is a catalog
 * with standalone services but no L1 the category list could show, so the
 * services screen replaces an empty list. A flat list needs a listable L1 to
 * group its services under.
 */
export function getServiceListMode({
  services,
  rows,
  requireBarberAssignment,
}: GetServiceListModeParams): ServiceListMode {
  const standaloneCount = services.filter(
    (service) =>
      !service.addonOnly &&
      !isAddonService(service) &&
      (!requireBarberAssignment || hasBarberAssignment(service)),
  ).length;
  if (standaloneCount === 0) return 'categories';

  const hasListableL1 =
    getL1CategoriesWithSubcategories({
      rows,
      services,
      cartServices: [],
      requireBarberAssignment,
    }).length > 0;
  if (!hasListableL1) return 'plain';

  return standaloneCount <= FLAT_LIST_MAX_SERVICES ? 'flat' : 'categories';
}
